import type { ScreenCompletion } from "@wishlink/applens-core";
import { safely } from "./contracts";
import { parseInjectedObservability } from "./injectedObservability";

type NavigationTiming = Pick<
  PerformanceNavigationTiming,
  "type" | "responseEnd" | "domContentLoadedEventEnd"
>;
export type DocumentTimings = Partial<
  Record<
    | "app.document_to_home_start_ms"
    | "app.document_to_content_ms"
    | "app.document_to_ready_ms"
    | "app.document.response_end_ms"
    | "app.document.dom_ready_ms"
    | "app.launch_to_document_ms",
    number
  >
>;
const milliseconds = (value: unknown): number | undefined =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 120000
    ? Math.round(value)
    : undefined;

/** One instance per document; the terminal reader supplies validated fields to SDK beta.4. */
export function createDocumentTimings(deps: {
  timeOrigin: number;
  now(): number;
  navigation(): NavigationTiming | undefined;
  injected?: unknown;
}) {
  let claimed = false;
  let restored = false;
  return {
    // NavigationTiming.type can remain "navigate" on a persisted pageshow.
    restoredFromBFCache() {
      restored = true;
    },
    startHome() {
      if (claimed) return;
      // Consume the first Home even if timing data or telemetry is unavailable.
      claimed = true;
      return safely(() => {
        const navigation = deps.navigation();
        if (
          restored ||
          !navigation ||
          !["navigate", "reload"].includes(navigation.type) ||
          !Number.isFinite(deps.timeOrigin) ||
          deps.timeOrigin <= 0
        )
          return;
        const start = deps.now();
        const startMs = milliseconds(start);
        if (startMs === undefined) return;
        const injected = parseInjectedObservability(
          deps.injected,
          deps.timeOrigin
        );
        let content: number | undefined;
        let finished: DocumentTimings | undefined;
        return {
          content() {
            safely(() => {
              if (restored || finished || content !== undefined) return;
              const now = deps.now();
              if (now >= start && milliseconds(now) !== undefined)
                content = now;
            }, undefined);
          },
          finish(outcome: ScreenCompletion["outcome"]): DocumentTimings {
            if (restored) return {};
            if (finished) return { ...finished };
            finished = { "app.document_to_home_start_ms": startMs };
            if (content !== undefined)
              finished["app.document_to_content_ms"] = Math.round(content);
            safely(() => {
              const now = deps.now();
              if (
                ["ready", "degraded"].includes(outcome) &&
                now >= (content ?? start)
              ) {
                const ready = milliseconds(now);
                if (ready !== undefined)
                  finished!["app.document_to_ready_ms"] = ready;
              }
            }, undefined);
            // Read again at terminal time: DOMContentLoaded may still be zero at route commit.
            const timing = safely(deps.navigation, undefined);
            for (const [key, field] of [
              ["app.document.response_end_ms", "responseEnd"],
              ["app.document.dom_ready_ms", "domContentLoadedEventEnd"],
            ] as const) {
              const value = safely(() => timing?.[field], undefined);
              const valid = milliseconds(value);
              // Navigation Timing uses zero for milestones that have not happened.
              if (value !== undefined && value > 0 && valid !== undefined)
                finished[key] = valid;
            }
            if (
              injected.launchSurface === true &&
              injected.launchStartEpochMs !== undefined
            ) {
              const launch = milliseconds(
                deps.timeOrigin - injected.launchStartEpochMs
              );
              if (launch !== undefined)
                finished["app.launch_to_document_ms"] = launch;
            }
            return { ...finished };
          },
        };
      }, undefined);
    },
  };
}
